import { useEffect, useState } from "react";
import { useAuth } from "@/auth-provider";
import { Link, useNavigate } from "react-router-dom";
import { Mail, ArrowRight, MailCheck, RotateCw, ArrowLeft } from "lucide-react";
import { AuthCard, AuthInput, AuthButton } from "@/components/auth/auth-form";

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");
  const [resendCooldown, setResendCooldown] = useState(0);
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (user) navigate("/");
  }, [user, navigate]);

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const t = setTimeout(() => setResendCooldown((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [resendCooldown]);

  const submit = async (e?: React.FormEvent) => {
    e?.preventDefault();
    setLoading(true);
    setError("");
    try {
      const res = await fetch("/api/auth/forgot-password", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim() }),
      });
      if (!res.ok) {
        setError("Something went wrong. Please try again.");
      } else {
        setSuccess(true);
        setResendCooldown(30);
      }
    } catch {
      setError("Network error — please try again.");
    }
    setLoading(false);
  };

  if (success) {
    return (
      <AuthCard
        eyebrow="Reset link sent"
        title="Check your inbox"
        subtitle={
          <>
            If an account exists for{" "}
            <span className="font-semibold text-foreground">{email}</span>, a reset link is
            on its way. Links expire in 60 minutes.
          </>
        }
        footer={
          <Link
            to="/login"
            className="flex items-center justify-center gap-1.5 text-sm font-semibold text-primary hover:underline"
          >
            <ArrowLeft className="h-3.5 w-3.5" />
            Back to sign in
          </Link>
        }
      >
        <div className="flex flex-col items-center text-center py-3">
          <div className="relative mb-5">
            <div className="absolute inset-0 rounded-full bg-emerald-500/20 blur-xl" />
            <div className="relative flex h-16 w-16 items-center justify-center rounded-2xl bg-emerald-500/10 ring-1 ring-emerald-500/30">
              <MailCheck className="h-8 w-8 text-emerald-400" />
            </div>
          </div>
          <p className="text-xs text-muted-foreground max-w-xs leading-relaxed">
            Don't see it? Check spam, or wait a moment — delivery can take up to a minute.
          </p>
          <button
            type="button"
            onClick={() => submit()}
            disabled={resendCooldown > 0 || loading}
            className="mt-5 inline-flex items-center gap-1.5 rounded-full border border-border/50 bg-background/40 px-4 py-1.5 text-xs font-semibold hover:bg-background/70 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <RotateCw className={"h-3 w-3 " + (loading ? "animate-spin" : "")} />
            {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : "Resend email"}
          </button>
        </div>
      </AuthCard>
    );
  }

  return (
    <AuthCard
      eyebrow="Forgot password"
      title="Reset your password"
      subtitle="Enter the email on your account and we'll send you a secure reset link."
      footer={
        <Link
          to="/login"
          className="flex items-center justify-center gap-1.5 text-sm font-semibold text-primary hover:underline"
        >
          <ArrowLeft className="h-3.5 w-3.5" />
          Back to sign in
        </Link>
      }
    >
      <form onSubmit={submit} className="space-y-4">
        <AuthInput
          label="Email"
          type="email"
          autoComplete="email"
          required
          icon={<Mail className="h-4 w-4" />}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@example.com"
        />

        {error && (
          <div className="rounded-xl border border-rose-500/30 bg-rose-500/10 px-3.5 py-2.5 text-xs text-rose-300">
            {error}
          </div>
        )}

        <AuthButton type="submit" loading={loading}>
          Send reset link <ArrowRight className="h-4 w-4" />
        </AuthButton>

        <p className="text-[11px] text-center text-muted-foreground">
          Remembered it?{" "}
          <Link to="/login" className="text-primary font-semibold hover:underline">
            Sign in instead
          </Link>
        </p>
      </form>
    </AuthCard>
  );
}
